import { z } from "zod";
import {
  parseProject,
  testSelectionSchema,
  testSourceRefSchema,
  type Project,
} from "./schema";
import type { PublishedVersion, Revision } from "./repository";

/**
 * Which definition Live proof, "Test this form" and impersonation load.
 *
 * - `published`: the latest published version (the long-standing default).
 * - `current`: the current, unpublished draft.
 * - `version:<id>`: one published version.
 * - `revision:<id>`: one retained draft revision from the document history.
 *
 * `form` mirrors the form from another source onto the selected document;
 * omit it to use the document's own form.
 */
export type TestSourceRef = z.infer<typeof testSourceRefSchema>;
export type TestSelection = z.infer<typeof testSelectionSchema>;

export const isDraftRef = (ref: TestSourceRef | undefined) =>
  ref === "current" || !!ref?.startsWith("revision:");
export const selectionUsesDrafts = (selection: TestSelection) =>
  isDraftRef(selection.document) || isDraftRef(selection.form);

/** Bounded parse: anything malformed becomes undefined so callers fall back to the default. */
export function parseTestSelection(input: unknown): TestSelection | undefined {
  const parsed = testSelectionSchema.safeParse(input);
  if (!parsed.success) return;
  const { document, form } = parsed.data;
  return form && form !== document ? { document, form } : { document };
}

const formSettingKeys = [
  "formSectionLayout",
  "formColumns",
  "formColumnWidth",
  "formAlignX",
  "formAlignY",
] as const;

/**
 * Which definition a test or proof window uses. `published` selects an
 * immutable published version; `revision` a retained draft; `draft` the
 * current working draft. `form` optionally takes the form from another source
 * while the document (pages, styles, assets, outputs) comes from `document`.
 */
export type TestSource =
  | { kind: "draft" }
  | { kind: "published"; versionId: string }
  | { kind: "revision"; revisionId: string };
/**
 * Mirror the form (fields, sections, tabs, rules, parts, datasets, reusable
 * forms and form layout settings) from `form` onto `document`'s artwork.
 * Identity, revision and status stay with the document. Variables the
 * artwork needs that the mirrored form lacks are kept from the document, out
 * of any section, and listed in `retained`.
 */
export function mirrorProjectForm(
  document: Project,
  form: Project,
): { project: Project; retained: string[] } {
  if (document.id !== form.id)
    throw new Error("Mirror a form from the same document only");
  const settings = { ...document.settings } as Record<string, unknown>;
  for (const key of formSettingKeys) {
    const value = (form.settings as Record<string, unknown>)[key];
    if (value === undefined) delete settings[key];
    else settings[key] = value;
  }
  const defined = new Set(form.fields.map((field) => field.id));
  const keys = new Set(form.fields.map((field) => field.key));
  const retainedFields = document.fields
    .filter((field) => !defined.has(field.id))
    .map((field) => ({
      ...field,
      groupId: undefined,
      // Keep variable keys unique when the mirrored form reused one.
      key: keys.has(field.key) ? `${field.key}_document` : field.key,
    }));
  const project = parseProject(
    structuredClone({
      ...document,
      fields: [...form.fields, ...retainedFields],
      groups: form.groups,
      tabs: form.tabs,
      triggers: form.triggers,
      parts: form.parts,
      datasets: form.datasets,
      forms: form.forms,
      formInstances: form.formInstances,
      dynamicSources: form.dynamicSources,
      resourceSnapshots: form.resourceSnapshots,
      settings,
    }),
  );
  return { project, retained: retainedFields.map((field) => field.id) };
}

export const testSourceLabel = (
  source: TestSource,
  names: {
    versions?: Record<string, number>;
    revisions?: Record<string, number>;
  } = {},
) =>
  source.kind === "draft"
    ? "Current draft"
    : source.kind === "published"
      ? `Published v${names.versions?.[source.versionId] ?? "?"}`
      : `Draft revision ${names.revisions?.[source.revisionId] ?? "?"}`;

/** The document's artwork with the form from `form` (see mirrorProjectForm). */
export const mirrorForm = (document: Project, form: Project): Project =>
  mirrorProjectForm(document, form).project;

export interface TestSources {
  current?: Project;
  versions: PublishedVersion[];
  revisions?: Revision[];
}
export interface ResolvedSource {
  ref: TestSourceRef;
  definition: Project;
  draft: boolean;
  label: string;
  versionId?: string;
}
export interface ResolvedTestSelection {
  selection: TestSelection;
  project: Project;
  /** Set only when document and form are the same published version, so proofs and generation stay immutable. */
  versionId?: string;
  draft: boolean;
  mirrored: boolean;
  label: string;
  document: ResolvedSource;
  form: ResolvedSource;
  /** The requested selection could not be used; the result fell back to the default. */
  fallback?: string;
}

const latest = (versions: PublishedVersion[]) =>
  versions.reduce<PublishedVersion | undefined>(
    (best, row) => (!best || row.version > best.version ? row : best),
    undefined,
  );

export function resolveTestSource(
  ref: TestSourceRef,
  sources: TestSources,
): ResolvedSource | undefined {
  if (ref === "published") {
    const version = latest(sources.versions);
    return (
      version && {
        ref,
        definition: version.definition,
        draft: false,
        label: `Latest published (v${version.version})`,
        versionId: version.id,
      }
    );
  }
  if (ref === "current")
    return (
      sources.current && {
        ref,
        definition: sources.current,
        draft: true,
        label: "Current draft",
      }
    );
  if (ref.startsWith("version:")) {
    const version = sources.versions.find((row) => row.id === ref.slice(8));
    return (
      version && {
        ref,
        definition: version.definition,
        draft: false,
        label: `Published v${version.version}`,
        versionId: version.id,
      }
    );
  }
  const revision = sources.revisions?.find((row) => row.id === ref.slice(9));
  return (
    revision && {
      ref,
      definition: revision.definition,
      draft: true,
      label: `Draft revision ${revision.revision}`,
    }
  );
}

/** The project default when nothing is stored: latest published, else the current draft for authors. */
export function defaultTestSelection(
  sources: TestSources,
  allowDrafts: boolean,
): TestSelection | undefined {
  if (sources.versions.length) return { document: "published" };
  if (allowDrafts && sources.current) return { document: "current" };
}

/**
 * Resolves a selection against the available definitions. Drafts resolve only when
 * `allowDrafts` is true (accounts that may manage documents); otherwise, and when a
 * stored reference no longer exists, the result falls back to the default.
 */
export function resolveTestSelection(
  requested: TestSelection | undefined,
  sources: TestSources,
  allowDrafts: boolean,
): ResolvedTestSelection | undefined {
  const attempt = (selection: TestSelection) => {
    if (!allowDrafts && selectionUsesDrafts(selection)) return;
    const document = resolveTestSource(selection.document, sources);
    const form = selection.form
      ? resolveTestSource(selection.form, sources)
      : document;
    if (!document || !form) return;
    const mirrored =
      !!selection.form &&
      (document.versionId
        ? document.versionId !== form.versionId
        : document.definition !== form.definition);
    const versionId =
      !mirrored && document.versionId ? document.versionId : undefined;
    return {
      selection,
      project: mirrored
        ? mirrorForm(document.definition, form.definition)
        : document.definition,
      versionId,
      draft: document.draft || form.draft,
      mirrored,
      label: mirrored
        ? `${document.label} · form from ${form.label}`
        : document.label,
      document,
      form,
    } satisfies ResolvedTestSelection;
  };
  if (requested) {
    const result = attempt(requested);
    if (result) return result;
  }
  const fallback = defaultTestSelection(sources, allowDrafts);
  const result = fallback && attempt(fallback);
  return (
    result &&
    (requested
      ? {
          ...result,
          fallback:
            !allowDrafts && selectionUsesDrafts(requested)
              ? "Drafts are limited to accounts that may manage documents."
              : "The saved selection is no longer available.",
        }
      : result)
  );
}
